// `floors` PUT dispatch for the offline New Room flow.
//
// resolveFloorLocal INSERTs an optimistic per-building floors row with a
// device-generated id, then this connector POSTs the same id to
// /capture/floors/resolve (find-or-create). The backend honours the
// client-supplied id (apps/backend/src/capture/resolve-floor.ts), so the
// server floors row shares the id the optimistic local space already
// FK's to. The floor PUT is uploaded before the dependent space PUT
// because PowerSync preserves op order (oldest first) and resolveFloor
// runs before the createSpace dispatch in the dialog.
//
// SESSION RESOLUTION (mirrors dispatchSpaceInsert). The floors table
// doesn't carry the session id, so the connector recovers it. A floor
// created offline is STAMPED with its origin session in the device-local
// floor_origin_sessions table (resolve-floor-local.ts; floors is admin
// reference data, so no synced column — see schema.ts). We resolve THAT
// session: its durable 409 re-home if the origin orphan was re-homed
// (getSessionRehome — survives an app reload via hydrateSessionRehomes),
// else the origin row itself if it still exists locally (open OR completed;
// #664 lets a late create land on a completed session).
//
// NEVER THROW on a missing session — that throw was the queue wedge this
// file exists to remove. uploadData dispatches a CRUD batch entry-by-entry
// and only acks once ALL succeed, so any throw on an EXPECTED condition makes
// the SDK retry the whole batch forever, blocking every queued count behind
// the floor PUT (the #656 wedge class). The two triggers, both at the old
// `sessionId === null → throw` site:
//   C2 (#690, cross-batch): a >100-op session or an app reload splits the
//     floor PUT into a later batch than its session's 409 re-home. The
//     per-batch context is empty and a building-wide SELECT can miss the
//     not-yet-synced authoritative session → the old code threw. Now the
//     durable re-home registry (or the still-present origin row) resolves it
//     and the POST lands — the count is preserved, not wedged.
//   H1 (#693, cancelled origin): the surveyor creates a room on a NEW floor
//     then cancels the session. cancelSession deletes capture_items +
//     capture_sessions but leaves the queued floors PUT + local floors row;
//     the origin session is gone. We do NOT fall back to the building's latest
//     session for a floor that knows its (now-gone) origin — that would
//     misattribute a cancelled session's floor to an unrelated session, the
//     mirror of the spaces leg's Codex-#2 cancelled-room hazard. So
//     origin-gone-and-not-re-homed ⟹ cancel intent wins: DROP the orphaned
//     floor (ack, no POST). A floor carries no captured COUNT — it is an
//     admin-curated name, and the dependent room is dropped by its own (#669)
//     cancelled-origin path with its counts held in the create-item not_found
//     quarantine — so dropping the floor is not contractual data loss. (Edge,
//     accepted for spaces-parity: a SIBLING live session on the same building
//     that reused this not-yet-synced, then-dropped floor — against
//     one-surveyor-per-building / ADR-003 — has its dependent space PUT DRAIN,
//     not wedge. dropOrphanFloor deletes the local floor, so the sibling space
//     PUT FK-fails server-side → assertFloorOnBuilding throws
//     FloorNotOnBuildingError → not_found (C6/#692 maps it to notFound, NOT
//     invalid_argument; create-space-error-mapping.ts). not_found is permanent
//     for EVERY table, so isPermanentUploadError(err, 'spaces') is true → the
//     central backstop's isolatePoisonEntry DELETEs the local space + emits
//     `dropped` rather than wedging the queue — the same clean cascade the
//     C6/#692 note below documents. The sibling room itself carries no count
//     (its quantities ride on capture_items, each preserved by its own
//     create-item not_found quarantine), so "never silently drop a count" +
//     ADR-009 still hold. Pre-existing as an edge; reaching it needs two
//     surveyors on one building, which the policy forbids; #745.)
// A legacy floor queued before the stamp shipped has no origin stamp; it
// falls back to the building's session (#664, includeCompleted=true), and if
// even that can't place it, it is dropped (ack) rather than wedging.
//
// 5xx / 4xx FROM THE POST are unchanged AT THIS DISPATCHER: they still throw.
// What the central backstop (connector-upload-isolation.ts) does with that throw
// is table-aware. C6/#692 (FIXED): an off-catalogue floor name → 400
// invalid_argument (resolveFloorCore → unknown_option) is now a PERMANENT drain
// for floors (PERMANENT_FLOORS_CODES) — the dispatcher rethrows and
// isolatePoisonEntry drains it so a renamed/deleted floor option can't wedge the
// device queue. The dependent New-Room room cascades cleanly: its spaces PUT FK's
// to the dropped floor, so createSpace returns 404 not_found
// (FloorNotOnBuildingError → notFound, create-space.ts) and the connector drains
// the space too (spaces treats not_found as permanent). A genuine session
// ownership/state failure (permission_denied /
// failed_precondition) deliberately stays transient and rethrows to the SDK
// (surfaces, never silently drained). We don't inspect the resolved id: in the
// excluded "two surveyors, same (building, name)" race the server returns the
// surviving floor's id (which may differ from ours); the dependent space PUT then
// FK-fails loudly rather than being silently dropped.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { apiFetch } from '../api'

import type { DispatchContext } from './connector-dispatch-context'
import { loadSessionIdForBuilding } from './connector-dispatch-space'
import { getSessionRehome } from './connector-session-rehome-registry'
import { _emitDropped, _emitUploaded } from './upload-events'

interface FloorResolvePostBody {
  sessionId: string
  floorName: string
  floorId: string
}

export async function dispatchFloorInsert(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  const data = entry.opData ?? {}

  const buildingId = data['building_id']
  if (typeof buildingId !== 'string' || buildingId.length === 0) {
    throw new Error(
      `dispatchFloorInsert: floors row ${entry.id} is missing building_id; ` +
        `the local INSERT (resolveFloorLocal) must populate it from the ` +
        `active capture session.`,
    )
  }

  const floorName = data['name']
  if (typeof floorName !== 'string' || floorName.length === 0) {
    throw new Error(
      `dispatchFloorInsert: floors row ${entry.id} is missing name; the ` +
        `local INSERT must carry the admin-curated floor option name.`,
    )
  }

  // Recover the live session, or DROP a cancelled-origin / unplaceable floor
  // (ack, never throw — see the file header). null ⟹ already dropped + acked.
  const sessionId = await resolveFloorSession(entry, db, context, buildingId)
  if (sessionId === null) return

  const body: FloorResolvePostBody = {
    sessionId,
    floorName,
    floorId: entry.id,
  }

  await apiFetch('/capture/floors/resolve', {
    method: 'POST',
    json: body,
  })
  _emitUploaded(entry.table, entry.id)
}

/**
 * Resolve the live capture session this queued floor PUT belongs to. Returns
 * the session id to POST against, or null when the floor was DROPPED (its local
 * row removed + a `dropped` event emitted) and the caller must ack without
 * POSTing. Mirrors dispatchSpaceInsert. NEVER throws — a throw here is the
 * C2/#690 + H1/#693 queue wedge.
 */
async function resolveFloorSession(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
  buildingId: string,
): Promise<string | null> {
  const createdInSessionId = await readFloorOriginSession(db, entry.id)

  if (createdInSessionId !== null) {
    // Stamped floor: resolve its OWN origin session. We deliberately do NOT
    // fall back to the building's latest session here — a cancelled origin must
    // drop, not re-home onto an unrelated session (spaces Codex #2).
    const rehomed = getSessionRehome(createdInSessionId)?.authoritativeId ?? null
    if (rehomed !== null) return rehomed
    const rows = await db.getAll<{ id: string }>(
      `SELECT id FROM capture_sessions WHERE id = ? LIMIT 1`,
      [createdInSessionId],
    )
    const originSessionId = rows[0]?.id ?? null
    if (originSessionId !== null) return originSessionId
    // Origin gone (cancelled / hard-deleted) and not re-homed ⟹ cancel intent
    // wins (H1/#693).
    return dropOrphanFloor(
      entry,
      db,
      buildingId,
      createdInSessionId,
      'powersync_floor_create_origin_cancelled_drop',
      'origin session cancelled (no revive)',
    )
  }

  // Unstamped (legacy) floor: recover the building's session (#664,
  // includeCompleted=true) — the cross-batch / app-reload path C2 depends on.
  const sessionId = await loadSessionIdForBuilding(db, buildingId, context, true)
  if (sessionId !== null) return sessionId
  // No origin stamp to confirm a cancel and no session resolvable: the floor
  // can't be placed. It carries no captured COUNT (the dependent room's counts
  // preserve themselves via the create-item not_found quarantine), so drop it
  // (ack) rather than wedge the whole queue.
  return dropOrphanFloor(
    entry,
    db,
    buildingId,
    null,
    'powersync_floor_create_unplaceable_drop',
    'no capture session resolvable for building (unstamped floor)',
  )
}

/**
 * Read the floor's origin-session stamp from the device-local
 * floor_origin_sessions table (resolve-floor-local.ts writes it on create).
 * Returns null when unstamped (a legacy floor queued before the stamp shipped),
 * so the caller degrades to the building-lookup fallback rather than throwing.
 */
async function readFloorOriginSession(
  db: PowerSyncWriteDb,
  floorId: string,
): Promise<string | null> {
  const rows = await db.getAll<{ origin_session_id: string | null }>(
    `SELECT origin_session_id FROM floor_origin_sessions WHERE id = ? LIMIT 1`,
    [floorId],
  )
  const originId = rows[0]?.origin_session_id
  return typeof originId === 'string' && originId.length > 0 ? originId : null
}

/**
 * Drop a queued floor PUT that can never land: delete the optimistic local
 * floors row (so it doesn't linger as a phantom after a building reopen — the
 * spaces leg deletes its row for the same reason; the resulting floors DELETE
 * CRUD entry acks LOCALLY in connector.ts's floors-DELETE branch, no server
 * POST), record telemetry, and emit `dropped` (superseded — suppressed from
 * the toast like the spaces cancelled-origin drop: a floor name is admin
 * reference data, not lost field work). Returns null so the caller acks without
 * POSTing. NEVER throws.
 *
 * We deliberately do NOT delete the floor_origin_sessions stamp here. uploadData
 * acks the whole CRUD batch only after EVERY entry succeeds, so a transient 5xx
 * on ANY sibling entry rethrows the batch and the SDK re-runs it — re-dispatching
 * this same (still-queued) floor PUT. If the drop had deleted the stamp, the
 * retry would read no stamp, fall through to the unstamped building-lookup
 * branch, and resolve an UNRELATED session for the building — resurrecting the
 * cancelled floor on a session it never belonged to (audit-#740: the spaces leg
 * is immune because its origin signal rides in immutable CRUD opData; ours is a
 * row, so it must survive the retry). Retaining the stamp makes the re-drop
 * idempotent. The stamp is local-only and keyed by the globally-unique floor id,
 * so a retained-after-drop row can never mis-match a future floor; it is dead
 * weight at worst, GC'd whenever the floor id is confirmed gone.
 */
async function dropOrphanFloor(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  buildingId: string,
  createdInSessionId: string | null,
  reason: string,
  detail: string,
): Promise<null> {
  await db.execute(`DELETE FROM floors WHERE id = ?`, [entry.id])
  Sentry.captureMessage(`[powersync] floors create dropped — ${detail}`, {
    level: 'warning',
    extra: { reason, entryId: entry.id, createdInSessionId, buildingId },
  })
  console.warn(
    `[powersync] floors create dropped — ${detail}; not wedging the queue`,
    { id: entry.id, createdInSessionId, buildingId },
  )
  _emitDropped(entry.table, entry.id, 'superseded')
  return null
}
